import React from 'react';

import { brand } from '@brand';

import { Box, Card, CardContent, Container, useTheme } from '@mui/material';

import { useRevealSecret } from '@/hooks';

import { isSecurityKeyChallenge } from '@/utils/webauthn/webauthn';

import { ErrorDisplay, Loading, RevealHeader } from '@/components/common';
import { PinInput, RevealButton, ViewSecret } from '@/components/secret';
import SecretDisplayWithTimeout from '@/components/secret/SecretDisplayWithTimeout.tsx';

import { styles } from './styles';

import type { PinType, SecretDetails } from '@/types';

// =============================================================================
// Helpers
// =============================================================================

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const resolvePinType = (paramsPinType: unknown, details?: SecretDetails): PinType => {
    if (details) {
        const { emailVerification, securityKey, keys, pin } = details;

        if (emailVerification === true) return 'email';
        if (isPlainObject(emailVerification) && emailVerification.id) return 'email';

        if (
            isSecurityKeyChallenge(details) ||
            (Array.isArray(securityKey) && securityKey.length > 0) ||
            (Array.isArray(keys) && keys.length > 0)
        ) {
            return 'securityKey';
        }

        if (isPlainObject(pin)) {
            if ((pin as { dynamic?: string }).dynamic) return 'dynamic';
            if ((pin as { static?: string }).static) return 'static';
        }

        if (typeof pin === 'string') {
            const normalized = pin.toLowerCase();
            if (normalized.includes('dynamic')) return 'dynamic';
            if (normalized.includes('static')) return 'static';
        }
    }

    if (typeof paramsPinType === 'string') {
        const normalized = paramsPinType.toLowerCase();
        if (normalized === 'dynamic' || normalized === 'dpin') return 'dynamic';
        if (normalized === 'email') return 'email';
        if (['security_key', 'security-key', 'securitykey'].includes(normalized)) return 'securityKey';
    }

    return 'static';
};

// =============================================================================
// Footer Component (declared outside to avoid recreation on each render)
// =============================================================================

const Footer = () => (
    <Box sx={styles.footer}>
        Secured by{' '}
        <Box component="a" href={brand.homeUrl} target="_blank" rel="noopener noreferrer" sx={styles.footerLink}>
            {brand.name}
        </Box>
    </Box>
);

// =============================================================================
// RevealPage Component
// =============================================================================

export const RevealPage = () => {
    const theme = useTheme();

    const {
        status,
        urlParams,
        decryptedSecret,
        isLoading,
        isDecrypting,
        pinError,
        serverError,
        apiResponse,
        emailVerification,
        emailGuess,
        handleView,
        submitPin,
        submitEmailGuess,
        submitEmailCode,
        revealSecret,
        downloadFile,
        decryptedFile,
    } = useRevealSecret();

    const pinType = React.useMemo<PinType>(() => {
        return resolvePinType(urlParams?.pinType, apiResponse?.details);
    }, [apiResponse?.details, urlParams?.pinType]);

    // =========================================================================
    // Render Content
    // =========================================================================

    const renderContent = () => {
        switch (status) {
            case 'loading':
                return <Loading message="Loading secret..." />;

            case 'start':
                return <ViewSecret onView={handleView} isLoading={isLoading} />;

            case 'pin_required':
                return (
                    <PinInput
                        mode="pin"
                        pinType={pinType}
                        onSubmitPin={submitPin}
                        error={pinError}
                        isLoading={isLoading}
                        securityKeyChallenge={
                            isSecurityKeyChallenge(apiResponse?.details) ? apiResponse?.details : undefined
                        }
                    />
                );

            case 'pin_email_guess':
                return (
                    <PinInput
                        mode="email_guess"
                        pinType="email"
                        onSubmitEmailGuess={submitEmailGuess}
                        onSubmitEmailCode={submitEmailCode}
                        emailGuessValue={emailGuess}
                        error={pinError}
                        isLoading={isLoading}
                        emailHintEnabled={emailVerification?.hintEnabled}
                        emailHintValue={emailVerification?.hintValue}
                    />
                );

            case 'pin_email_code':
                return (
                    <PinInput
                        mode="email_code"
                        pinType="email"
                        onSubmitEmailGuess={submitEmailGuess}
                        onSubmitEmailCode={submitEmailCode}
                        error={pinError}
                        isLoading={isLoading}
                        emailHintEnabled={emailVerification?.hintEnabled}
                        emailHintValue={emailVerification?.hintValue}
                    />
                );

            case 'ready':
                return (
                    <RevealButton
                        publicDescription={apiResponse?.details?.publicDescription}
                        isOneTimeLink={apiResponse?.details?.otl}
                        onReveal={revealSecret}
                        isLoading={isDecrypting}
                    />
                );

            case 'revealed':
                if (!decryptedSecret) {
                    return <ErrorDisplay status="error" message={pinError} />;
                }

                // ✅ NEW: Use SecretDisplayWithTimeout
                return (
                    <SecretDisplayWithTimeout
                        secret={decryptedSecret}
                        timeout={apiResponse?.details?.timeout}
                        onDownloadFile={downloadFile}
                        onDecryptFile={decryptedFile}
                        onTimeoutExpire={() => {
                            // Optional: log, analytics, etc.
                            console.log('Viewing time expired');
                        }}
                    />
                );

            // Locked, no attempts left or not open yet: the server's own words say which.
            case 'unavailable':
                return <ErrorDisplay status={status} message={serverError} />;

            case 'not_found':
            case 'expired':
            case 'already_viewed':
            case 'erased':
            case 'error':
                return <ErrorDisplay status={status} message={pinError} />;

            default:
                return <ErrorDisplay status="error" message={pinError} />;
        }
    };

    // =========================================================================
    // Main Layout
    // =========================================================================

    return (
        <Box sx={styles.pageContainer}>
            <RevealHeader sender={apiResponse?.details?.org} />
            <Box sx={styles.gradientOverlay(theme.palette.primary.main)} />

            <Container maxWidth="sm" sx={styles.contentContainer}>
                <Card elevation={2} sx={styles.card}>
                    <CardContent sx={styles.cardContent}>{renderContent()}</CardContent>
                </Card>

                <Footer />
            </Container>
        </Box>
    );
};
